// noinspection JSUnusedGlobalSymbols

import type { UserType } from '@/types/enums'
import logger from '@/lib/logger'

/**
 * Safely accesses nested properties using dot path.
 */
export const safeGet = function<T = unknown>(obj: unknown, path: string, fallback?: T): T | undefined {
    if (!obj || !path) return fallback;
    const parts = path.split('.');
    let cur: unknown = obj;
    for (const p of parts) {
        if (cur == null) return fallback;
        cur = (cur as Record<string, unknown>)[p];
    }
    return cur === undefined ? fallback : cur as T;
}


/**
 * Deterministic color from a seed string. Returns hex color.
 */
export const avatarColor = function(seed = '', pastel = true): string {
    let h = 0;
    for (let i = 0; i < seed.length; i++) {
        // simple hash
         
        h = seed.charCodeAt(i) + ((h << 5) - h);
        // keep in 32-bit int range
         
        h = h & h;
    }
    const hue = Math.abs(h) % 360;
    const saturation = pastel ? 60 : 50;
    const lightness = pastel ? 70 : 50;
    // convert HSL to hex
    return hslToHex(hue, saturation, lightness);
};

const hslToHex = function(h: number, s: number, l: number): string {
    s /= 100;
    l /= 100;
    const k = (n: number) => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
     
    const f = (n: number) =>
        Math.round(255 * (l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))));

    const toHex = (v: number) => v.toString(16).padStart(2, '0');
    return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
}

/**
 * Normalizes user input for getUserRedirectUrl.
 */
const normalizeUser = function(user: UserType | { userType?: number | string } | null): { userType?: number | string } | null {
    if (user === null) return null;
    if (typeof user === 'object') return user as { userType?: number | string };
    return { userType: user as number | string };
};

/**
 * Converts userType to number for redirect logic.
 */
const getUserTypeNum = function(userObj: { userType?: number | string }): number | null {
    if (typeof userObj.userType === 'string') {
        switch (userObj.userType.toUpperCase()) {
            case 'ADMIN':
                return 1;
            case 'CUSTOMER':
                return 3;
            case 'COMPANY':
                return 2;
            default:
                return null;
        }
    } else {
        return userObj.userType as number;
    }
};

// Yeni yardımcı: gelen path parametresini güvenli ve anlamlı hale getirir.
const normalizeRedirectPath = function(raw?: string | null): string | null {
    if (!raw) return null;
    let decoded: string;
    try {
        // Bazı frameworkler query parametrelerini zaten decode eder, buna rağmen güvenli tarafta decode etmeye çalış
        decoded = decodeURIComponent(raw);
    } catch  {
        // ignore decode errors and use raw
        decoded = raw as string;
    }

    // Trim boşluklar
    decoded = decoded.trim();

    // Eğer root ("/") ise bunu null say; projede ana sayfa yok ve user-specific yönlendirme kullanılacak
    if (decoded === '/' || decoded === '') return null;

    // Güvenlik: yalnızca aynı origin içinde kalan relative path'lere izin ver (başında "/" olmalı, fakat "//" olmamalı)
    if (decoded.startsWith('/') && !decoded.startsWith('//')) {
        return decoded;
    }

    // Diğer tüm durumlar (tam URL veya dış kaynak) kabul edilmez
    return null;
};

/**
 * Returns redirect URL based on userType.
 * 1: ADMIN → /admin
* 2: COMPANY → /panel 
* 3: CUSTOMER → /panel
 * Default: /login
 */
export const getUserRedirectUrl = function(user: UserType | { userType?: number | string } | null, path?: string): string {
    const userObj = normalizeUser(user);

    logger.info('getUserRedirectUrl: user:', user, 'path:', path);
    if (!userObj) {
        logger.info('getUserRedirectUrl: returning /login because user is null');
        return '/login';
    }

    // If userType is not set, default to panel dashboard
    if (typeof userObj.userType !== 'number' && typeof userObj.userType !== 'string') {
        logger.info('getUserRedirectUrl: userType not set, defaulting to /panel');
        return '/console/home';
    }

    // Normalize and validate incoming path (handles encoded values like "%2F")
    const safePath = normalizeRedirectPath(path);
    if (safePath) {
        // Eğer geçerli ve site içi bir pathse doğrudan onu döndür
        logger.info('getUserRedirectUrl: using safe redirect path:', safePath);
        return safePath;
    }

    // Eğer gelen path root ("/") veya geçersizse, userType'a göre yönlendir
    const userTypeNum = getUserTypeNum(userObj);

    if (userTypeNum === null) {
        logger.info('getUserRedirectUrl: unknown userType, redirecting to /login');
        return '/login';
    }

    switch (userTypeNum) {
        case 1: // ADMIN
            return '/admin';
        case 2: // COMPANY
        case 3: // CUSTOMER
            return '/console/home';
        default:
            return '/login';
    }
};
